import { FC, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import $api from "../../shared/api/axios";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { AppSwitch, FigmaTypography } from "@libs/zapi-ui/custom";

type TOrg = {
  id: number;
  name: string;
  status: string;
};

type TFeatureFlagWithOverrides = {
  key: string;
  name: string;
  description: string;
  overrides: { organizationId: number; enabled: boolean }[];
};

type TFeatureFlagOrgState = {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
};

export const FeatureFlagsPage: FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") ?? "by-org";
  const orgIdParam = searchParams.get("orgId");
  const selectedOrgId = orgIdParam ? parseInt(orgIdParam) : null;
  const selectedFlagKey = searchParams.get("flagKey") ?? null;

  const setTab = (value: string) => setSearchParams((p) => { p.set("tab", value); return p; });
  const setOrgId = (id: number) => setSearchParams((p) => { p.set("tab", "by-org"); p.set("orgId", id.toString()); return p; });
  const setFlagKey = (key: string) => setSearchParams((p) => { p.set("tab", "by-flag"); p.set("flagKey", key); return p; });

  const { data: orgs = [] } = useQuery<TOrg[]>({
    queryKey: ["organizations-with-users"],
    queryFn: async () => {
      const response = await $api.get("/organizations/with-users");
      const all: TOrg[] = response?.data?.data || [];
      return all.filter((o) => o.status !== "archived");
    }
  });

  const { data: allFlags = [], isLoading: flagsLoading } = useQuery<TFeatureFlagWithOverrides[]>({
    queryKey: ["admin-feature-flags"],
    queryFn: async () => {
      const response = await $api.get("/admin/feature-flags");
      return response?.data || [];
    }
  });

  const { data: orgFlags = [], isLoading: orgFlagsLoading } = useQuery<TFeatureFlagOrgState[]>({
    queryKey: ["admin-feature-flags-by-org", selectedOrgId],
    queryFn: async () => {
      const response = await $api.get(`/admin/feature-flags/by-org/${selectedOrgId}`);
      return response?.data || [];
    },
    enabled: selectedOrgId !== null
  });

  const toggleMutation = useMutation({
    mutationFn: ({ flagKey, orgId, enabled }: { flagKey: string; orgId: number; enabled: boolean }) =>
      $api.put(`/admin/feature-flags/orgs/${orgId}/${flagKey}`, { enabled }),
    onSuccess: (_data, variables) => {
      toast.success("Feature flag updated");
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags"] });
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags-by-org", variables.orgId] });
    },
    onError: () => toast.error("Failed to update feature flag")
  });

  const bulkToggleOrgMutation = useMutation({
    mutationFn: ({ orgId, enabled }: { orgId: number; enabled: boolean }) =>
      $api.put(`/admin/feature-flags/orgs/${orgId}/bulk`, { enabled }),
    onSuccess: (_data, variables) => {
      toast.success(`All flags ${variables.enabled ? "enabled" : "disabled"} for this organization`);
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags"] });
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags-by-org", variables.orgId] });
    },
    onError: () => toast.error("Failed to bulk update flags")
  });

  const bulkToggleFlagMutation = useMutation({
    mutationFn: ({ flagKey, enabled }: { flagKey: string; enabled: boolean }) =>
      $api.put(`/admin/feature-flags/flags/${flagKey}/bulk`, { enabled }),
    onSuccess: () => {
      toast.success(`Flag ${selectedFlagKey} updated for all organizations`);
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags"] });
      if (selectedOrgId) queryClient.invalidateQueries({ queryKey: ["admin-feature-flags-by-org", selectedOrgId] });
    },
    onError: () => toast.error("Failed to bulk update flag")
  });

  const selectedFlag = selectedFlagKey ? allFlags.find((f) => f.key === selectedFlagKey) ?? null : null;

  const sortedOrgs = useMemo(() => {
    if (!selectedFlag) return orgs;
    const enabledSet = new Set(
      selectedFlag.overrides.filter((o) => o.enabled).map((o) => o.organizationId)
    );
    return [...orgs].sort((a, b) => Number(enabledSet.has(b.id)) - Number(enabledSet.has(a.id)));
  }, [orgs, selectedFlag]);

  const enabledOrgCount = selectedFlag
    ? orgs.filter((org) => selectedFlag.overrides.some((o) => o.organizationId === org.id && o.enabled)).length
    : 0;

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <FigmaTypography component="h1" variant="heading-1" className="mb-6">Feature Flags</FigmaTypography>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="by-org">By Organization</TabsTrigger>
          <TabsTrigger value="by-flag">By Flag</TabsTrigger>
        </TabsList>

        <TabsContent value="by-org">
          <div className="mb-6 max-w-xs">
            <label className="block text-[12px] leading-[18px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Organization</label>
            <Select
              value={selectedOrgId?.toString() ?? ""}
              onValueChange={(v) => setOrgId(parseInt(v))}
            >
              <SelectTrigger className="h-11 text-base font-medium border-2">
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((org) => (
                  <SelectItem key={org.id} value={org.id.toString()}>
                    {org.name} (#{org.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedOrgId !== null && !orgFlagsLoading && orgFlags.length > 0 && (() => {
            const count = orgFlags.filter((f) => f.enabled).length;
            const allEnabled = count === orgFlags.length;
            return (
              <div className="mb-3 flex items-center gap-4">
                <span className="text-[14px] text-gray-500">
                  Enabled flags: <span className="font-medium">{count} of {orgFlags.length}</span>
                </span>
                <div className="flex items-center gap-2">
                  <AppSwitch
                    checked={allEnabled}
                    onCheckedChange={(checked) => bulkToggleOrgMutation.mutate({ orgId: selectedOrgId, enabled: checked })}
                    disabled={bulkToggleOrgMutation.isPending}
                    className="data-[state=unchecked]:bg-innoveraZinc-200"
                  />
                  <FigmaTypography component="span" variant="body-3" className="text-gray-500">All flags</FigmaTypography>
                </div>
              </div>
            );
          })()}

          {selectedOrgId !== null && (
            <div className="rounded-lg border bg-card shadow-sm">
              {orgFlagsLoading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : orgFlags.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No feature flags defined yet</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Flag</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Description</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...orgFlags].sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.name.localeCompare(b.name)).map((f) => (
                      <tr key={f.key} className="border-b hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <button
                            className="text-[14px] font-medium text-blue-600 hover:underline text-left"
                            onClick={() => setFlagKey(f.key)}
                          >
                            {f.name}
                          </button>
                          <FigmaTypography component="div" variant="body-3" className="text-gray-400">{f.key}</FigmaTypography>
                        </td>
                        <td className="px-6 py-4 text-[14px] text-gray-600 max-w-xs">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="line-clamp-2 cursor-default">{f.description}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm">
                              <p>{f.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="px-6 py-4">
                          <AppSwitch
                            checked={f.enabled}
                            onCheckedChange={(checked) =>
                              toggleMutation.mutate({ flagKey: f.key, orgId: selectedOrgId, enabled: checked })
                            }
                            disabled={toggleMutation.isPending}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="by-flag">
          <div className="mb-6 max-w-sm">
            <label className="block text-[12px] leading-[18px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Feature Flag</label>
            <Select
              value={selectedFlagKey ?? ""}
              onValueChange={(v) => setFlagKey(v)}
            >
              <SelectTrigger className="h-11 text-base font-medium border-2">
                <SelectValue placeholder="Select a feature flag">
                  {selectedFlagKey ? (allFlags.find((f) => f.key === selectedFlagKey)?.name ?? selectedFlagKey) : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {allFlags.map((f) => (
                  <SelectItem key={f.key} value={f.key} textValue={f.name}>
                    <div className="py-0.5">
                      <div>{f.name}</div>
                      <FigmaTypography component="div" variant="body-3" className="text-gray-400">{f.key}</FigmaTypography>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedFlag !== null && (
            <>
              {selectedFlag.description && (
                <div className="mt-1 mb-4 text-[14px] text-gray-500">{selectedFlag.description}</div>
              )}
              <div className="mb-3 flex items-center gap-4">
                <span className="text-[14px] text-gray-500">
                  Enabled for: <span className="font-medium">{enabledOrgCount} {enabledOrgCount === 1 ? "org" : "orgs"}</span>
                </span>
                <div className="flex items-center gap-2">
                  <AppSwitch
                    checked={enabledOrgCount === orgs.length && orgs.length > 0}
                    onCheckedChange={(checked) => bulkToggleFlagMutation.mutate({ flagKey: selectedFlag.key, enabled: checked })}
                    disabled={bulkToggleFlagMutation.isPending}
                    className="data-[state=unchecked]:bg-innoveraZinc-200"
                  />
                  <FigmaTypography component="span" variant="body-3" className="text-gray-500">All orgs</FigmaTypography>
                </div>
              </div>
              <div className="rounded-lg border bg-card shadow-sm">
                {flagsLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Organization</th>
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Enabled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOrgs.map((org) => {
                        const override = selectedFlag.overrides.find((o) => o.organizationId === org.id);
                        const enabled = override?.enabled ?? false;
                        return (
                          <tr key={org.id} className="border-b hover:bg-gray-50">
                            <td className="px-6 py-4 text-[14px] font-medium">
                              <button
                                className="text-blue-600 hover:underline"
                                onClick={() => setOrgId(org.id)}
                              >
                                {org.name}
                              </button>
                              <span className="text-gray-400 font-normal ml-1">(#{org.id})</span>
                            </td>
                            <td className="px-6 py-4">
                              <AppSwitch
                                checked={enabled}
                                onCheckedChange={(checked) =>
                                  toggleMutation.mutate({ flagKey: selectedFlag.key, orgId: org.id, enabled: checked })
                                }
                                disabled={toggleMutation.isPending}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
